import { IStringTool } from "../core/IStringTool"

/**
 * Input for adaptive lerp factor calculation.
 * Maps a speed-like value to a lerp factor, where:
 * - lower speed ⇒ slower smoothing (higher lerp factor)
 * - higher speed ⇒ faster response (lower lerp factor)
 */
interface AdaptiveLerpInput {
  /** Current value (e.g., speed or delta). */
  value: number

  /** Minimum input threshold (default: 0.1) */
  inMin?: number

  /** Maximum input threshold (default: 1.0) */
  inMax?: number

  /** Output when input is at minimum (default: 0.65) */
  outMax?: number

  /** Output when input is at maximum (default: 0.05) */
  outMin?: number
}

/**
 * Converts a numeric input (like velocity) into an adaptive lerp factor.
 * Useful for scroll or speed-based smoothing effects.
 */
export default class AdaptiveLerpTool implements IStringTool<AdaptiveLerpInput, number> {
  /**
   * @returns A remapped lerp factor from `outMax` to `outMin`.
   */
  process({
    value,
    inMin = 0.1,
    inMax = 1.0,
    outMin = 0.05,
    outMax = 0.65
  }: AdaptiveLerpInput): number {
    if (value < inMin) return outMax
    if (value > 1.0) value = 1.0

    if (value <= inMax) {
      const t = (value - inMin) / (inMax - inMin)
      return outMax - t * (outMax - outMin)
    }

    return outMin
  }
}
